import type { DocumentConfig, DocumentData } from '@alpod-ai-writer/shared';
import * as Sentry from '@sentry/react';
import { Button, Modal, type ModalProps } from 'flowbite-react';
import { DownloadIcon } from 'lucide-react';
import { useCallback, useState } from 'react';

import { useAccessToken } from '../../../api/useAccessToken.hook';
import { useApi } from '../../../api/useApi.hook';
import { getDocumentExportRequestData } from '../../../editor/utils/export.utils';
import { ToastNotificationType } from '../../components/toastNotification/toastNotifications.context';
import { useToastNotifications } from '../../components/toastNotification/useToastNotifications.hook';

export interface ExportModalProps extends ModalProps {
    config: DocumentConfig;
    data: DocumentData;
    documentType?: string;
    documentName?: string;
}

export const ExportModal = ({
    config,
    data,
    documentType,
    documentName,
    show,
    onClose
}: ExportModalProps) => {
    const api = useApi();
    const toasts = useToastNotifications();
    const [ isLoading, setIsLoading ] = useState(false);
    const accessToken = useAccessToken();

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const doExport = useCallback((format: 'docx') => {
        setIsLoading(true);

        const body = getDocumentExportRequestData(config, data, documentType, documentName);

        fetch('/exporter/documents/generate', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
        }).then(async (response) => {
            if (response.status === 200) {
                const binaryData = await response.blob();
                const url = URL.createObjectURL(binaryData);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'export.docx';
                a.click();
            } else {
                console.error('Failed to export document', response);
                Sentry.captureMessage('Failed to export document', {
                    extra: {
                        status: response.status,
                        body: await response.text()
                    }
                });

                toasts.showToast({
                    type: ToastNotificationType.ERROR,
                    message: 'Nepodařilo se exportovat dokument. Zkuste to prosím za chvíli znovu nebo kontaktujte podporu.',
                });                
            }
        }, (err) => {
            console.error('Failed to export document', err);
            Sentry.captureException(err);
            toasts.showToast({
                type: ToastNotificationType.ERROR,
                message: 'Nepodařilo se exportovat dokument. Zkuste to prosím za chvíli znovu nebo kontaktujte podporu.',
            });
        }).finally(() => {
            setIsLoading(false);
        });
    }, [ api, data, accessToken ]);

    const handleDownloadDocx = useCallback(() => {
        doExport('docx');
    }, [ doExport ]);

    return (
        <Modal show={show} size="md" onClose={onClose} popup>
            <Modal.Header />
            <Modal.Body>
                <h3 className="mb-5 flex flex-row items-center justify-center gap-2 text-lg font-semibold text-center text-gray-800 dark:text-gray-400">
                    <DownloadIcon size={24} className='text-gray-800' />
                    <span>Vyberte formát</span>
                </h3>
                <div className="flex justify-center gap-4">
                    <Button
                        color="primary"
                        onClick={handleDownloadDocx}
                        disabled={isLoading}
                    >DOCX (MS Word)</Button>
                </div>
            </Modal.Body>
        </Modal>
    );
};
